import { SearchOutlined } from '@ant-design/icons';
import { Alert, Descriptions, Table, Tag } from 'antd';
import PropTypes from 'prop-types';
import React from 'react';
import ToolWrapper from './components/ToolWrapper';
import arrayToTableData from './utils/tableHelpers';

/**
 * Renders the FindVerbatims tool response.
 * Shows verbatim search results and matched quotes.
 */
function FindVerbatimsTool({ item }) {
  const searchContext = item.args?.search_context || '';
  const maxQuotes = item.args?.max_quotes || 0;
  const status = item.response?.status || '';
  const verbatimIds = item.response?.verbatim_ids || [];
  const searchSummary = item.response?.search_summary || '';
  const totalSearched = item.response?.total_searched || 0;
  const savedQuotes = item.response?.saved_quotes || [];
  const isPending = item.response === null || item.response === undefined;

  const { dataSource, columns } = arrayToTableData(savedQuotes);

  return (
    <ToolWrapper toolName="Verbatim Search" isPending={isPending} icon={<SearchOutlined />}>
      <Descriptions column={1} bordered size="small">
        <Descriptions.Item label="Context">{searchContext}</Descriptions.Item>
        <Descriptions.Item label="Max quotes requested">{maxQuotes}</Descriptions.Item>
        {!isPending && (
          <>
            <Descriptions.Item label="Status">
              <Tag color={status === 'success' ? 'green' : 'red'}>{status}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Total searched">{totalSearched}</Descriptions.Item>
            <Descriptions.Item label="Found">{verbatimIds.length}</Descriptions.Item>
            {searchSummary && (
              <Descriptions.Item label="Summary">{searchSummary}</Descriptions.Item>
            )}
          </>
        )}
      </Descriptions>
      {savedQuotes.length > 0 && (
        <Table
          dataSource={dataSource}
          columns={columns}
          pagination={{ pageSize: 10 }}
          size="small"
          style={{ marginTop: 16 }}
        />
      )}
      {verbatimIds.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <strong>Verbatim IDs:</strong>
          <ul style={{ fontSize: 12 }}>
            {verbatimIds.map((id) => (
              <li key={id}>{id}</li>
            ))}
          </ul>
        </div>
      )}
    </ToolWrapper>
  );
}

FindVerbatimsTool.propTypes = {
  item: PropTypes.shape({
    args: PropTypes.shape({
      search_context: PropTypes.string,
      max_quotes: PropTypes.number,
    }),
    response: PropTypes.shape({
      status: PropTypes.string,
      verbatim_ids: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
      search_summary: PropTypes.string,
      total_searched: PropTypes.number,
      saved_quotes: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.object, PropTypes.array])),
    }),
  }).isRequired,
};

/**
 * Renders the DisplayVerbatim tool response.
 * Shows detailed verbatim specification.
 */
function DisplayVerbatimTool({ item }) {
  const verbatimId = item.args?.verbatim_id || '';
  const status = item.response?.status || '';
  const verbatimSpec = item.response?.verbatim || null;
  const isPending = item.response === null || item.response === undefined;

  return (
    <div style={{ marginBottom: 16 }}>
      {isPending ? (
        <div style={{ fontSize: 13, color: '#8c8c8c' }}>Loading verbatim details...</div>
      ) : (
        <>
          <Descriptions column={1} bordered size="small">
            <Descriptions.Item label="Verbatim ID">{verbatimId}</Descriptions.Item>
            <Descriptions.Item label="Status">
              <Tag color={status === 'success' ? 'green' : 'red'}>{status}</Tag>
            </Descriptions.Item>
          </Descriptions>
          {verbatimSpec ? (
            <div style={{ marginTop: 16 }}>
              <strong>Verbatim Details:</strong>
              <pre
                style={{
                  background: '#f5f5f5',
                  padding: 12,
                  borderRadius: 4,
                  overflow: 'auto',
                  maxHeight: 400,
                  fontSize: 12,
                }}
              >
                {JSON.stringify(verbatimSpec, null, 2)}
              </pre>
            </div>
          ) : (
            <Alert message="No verbatim spec found" type="info" style={{ marginTop: 16 }} />
          )}
        </>
      )}
    </div>
  );
}

DisplayVerbatimTool.propTypes = {
  item: PropTypes.shape({
    args: PropTypes.shape({
      verbatim_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
    response: PropTypes.shape({
      status: PropTypes.string,
      verbatim_id: PropTypes.string,
      verbatim: PropTypes.shape({
        quote_text: PropTypes.string,
        context: PropTypes.string,
        response_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      }),
      render_annotation: PropTypes.shape({
        type: PropTypes.string,
        excerpt_25w: PropTypes.string,
        word_count: PropTypes.number,
        truncated: PropTypes.bool,
        context: PropTypes.string,
        source_response_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      }),
    }),
  }).isRequired,
};

export { FindVerbatimsTool, DisplayVerbatimTool };
